'use client'

import { RichTextBlockModel } from 'clients/strapi/models/BlockModels'
import clsx from 'clsx'
import { Breakpoint } from 'enums/Breakpoints.enum'
import { HTMLMotionProps, motion } from 'motion/react'
import ChevronPrimary from 'public/assets/chevron-down-primary.svg'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'
import styles from './TableOfContents.module.scss'

type TableOfContentsProps = {
  block: RichTextBlockModel
}

const useIsMounted = () => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true)
  }, [])

  return isMounted
}

const TableOfContents = ({ block }: TableOfContentsProps) => {
  const [currentHeadingId, setCurrentHeadingId] = useState('')
  const [currentHeadingName, setCurrentHeadingName] = useState('Contents')
  const [open, setOpen] = useState(false)
  const isMounted = useIsMounted()
  const [isSmallScreen, setIsSmallScreen] = useState(false)

  useEffect(() => {
    function check() {
      const w = window.innerWidth
      setIsSmallScreen(w < Breakpoint.MEDIUM_LARGE)
    }
    check()
    window.addEventListener('resize', check)

    return () => window.removeEventListener('resize', check)
  }, [])

  const containerRef = useRef<HTMLDivElement>(null)

  const goToHeading = (id: string) => {
    const headingElement = document.getElementById(id)
    if (!headingElement) return

    // Use manual scrollTo instead of scrollIntoView to avoid mobile browser
    // dynamic toolbar (collapsing/expanding address bar) causing scroll offset issues.
    // On smaller screens the sidebar (with ToC) is sticky under the header,
    // so we need to account for its height as additional offset.
    const headerHeight = isSmallScreen ? 60 : 100
    const sidebarEl = containerRef.current?.parentElement?.parentElement // .tocWrapper -> .sidebar
    const sidebarOffset = isSmallScreen && sidebarEl ? sidebarEl.getBoundingClientRect().height : 0
    const offset = headerHeight + sidebarOffset + 16
    const top = headingElement.getBoundingClientRect().top + window.scrollY - offset
    window.scrollTo({ top, behavior: 'smooth' })
  }

  const isActive = (id?: string) => currentHeadingId === id

  const toggle = () => setOpen(!open)

  useEffect(() => {
    function handleActiveHeadingChanged(event: CustomEvent<{ headingId: string }>) {
      const { headingId } = event.detail
      const foundHeading = document.getElementById(headingId)
      if (!foundHeading) {
        setCurrentHeadingId('')
        setCurrentHeadingName('Contents')

        return
      }

      const tocLink = document.getElementById(headingId + '-toc')
      if (tocLink) {
        document.getElementById('toc-links')?.scrollTo({ top: tocLink.offsetTop - 60, behavior: 'smooth' })
      }

      setCurrentHeadingId(headingId)
      setCurrentHeadingName(foundHeading.innerText!)
    }

    const activeHeadingChangedListener = (event: Event) => {
      const customEvent = event as CustomEvent<{ headingId: string }>
      if (!customEvent) return
      handleActiveHeadingChanged(customEvent)
    }

    window.addEventListener('active-heading-changed', activeHeadingChangedListener)

    return () => {
      window.removeEventListener('active-heading-changed', activeHeadingChangedListener)
    }
  }, [])

  const animation: HTMLMotionProps<'ul'> = {
    layout: 'position',
    animate: { opacity: isMounted ? 1 : 0 },
    initial: { opacity: 0 },
  }

  if (!isMounted) {
    return (
      <motion.div ref={containerRef} layoutRoot layout className={styles.container}>
        <motion.div layout='position' className={styles.toggle}>
          <span className={styles.title}>Contents</span>
          <motion.div className={styles.chevron}>
            <ChevronPrimary />
          </motion.div>
        </motion.div>
      </motion.div>
    )
  }

  return (
    <motion.div ref={containerRef} layoutRoot layout className={clsx(styles.container, { [styles.noScroll]: isSmallScreen && open })} onClick={isSmallScreen ? toggle : undefined}>
      <motion.div layout='position' className={styles.toggle} onClick={toggle}>
        <span className={styles.title}>{currentHeadingName !== '' && !open ? currentHeadingName : 'Contents'}</span>
        <motion.div className={styles.chevron} animate={{ rotateX: open ? 180 : 0 }}>
          <ChevronPrimary />
        </motion.div>
      </motion.div>
      {open && (
        <motion.ul {...animation} id='toc-links' className={styles.links}>
          <ReactMarkdown
            rehypePlugins={[[rehypeSlug], [rehypeRaw]]}
            allowedElements={['h2', 'h3']}
            components={{
              h2: (props) => {
                return (
                  <li id={props.id + '-toc'} className={clsx(styles.link, isActive(props.id) && styles.active)} onClick={() => goToHeading(props.id!)}>
                    {props.children}
                  </li>
                )
              },
              h3: (props) => {
                return (
                  <li
                    id={props.id + '-toc'}
                    className={clsx(styles.link, styles.sublink, isActive(props.id) && styles.active)}
                    onClick={() => goToHeading(props.id!)}
                  >
                    {props.children}
                  </li>
                )
              },
            }}
          >
            {block.body}
          </ReactMarkdown>
        </motion.ul>
      )}
    </motion.div>
  )
}

export default TableOfContents
